/**
Copyright (c) 2023, 2024, 2025 Amica Corp.
All rights reserved.

This source code, resulting binary forms, and associated documentation
(the "Software") may only be used, modified, or distributed, with or
without modification, under the terms of a written license agreement
with Amica Corp. (the "License"). All rights to manufacture, use,
redistribute, or sell anything described herein, in whole or in part,
are granted only as explicitly permitted by such License. The receipt
or possession of the Software does not convey any of these rights.

This Software is provided "AS IS," WITHOUT WARRANTY OF ANY KIND, express
or implied, including but not limited to the implied warranties of
MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.

For additional terms, see https://amica.cloud/software-copyright/
 */

/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import ERROR_TYPES from 'modules/common/constants/error-types';
import { forgotPasswordActions } from 'modules/forgot-password/slice';
import { hotelActions } from 'modules/hotels/slice';
import { resetPasswordActions } from 'modules/reset-password/slice';
import { scenarioActions } from 'modules/scenario/slice';
import { signInActions } from 'modules/sign-in/slice';
import { systemActions } from 'modules/system/slice';
import { userInvitesActions } from 'modules/user-invitation/slice';
import { usersActions } from 'modules/users/slice';
import { verifyCallbackActions } from 'modules/verify-callback/slice';
import { visualFilterActions } from 'modules/visual-filter/slice';
import { visualActions } from 'modules/visual/slice';
import { hotelSetActions } from '../../hotel-set/slice';
import { llmActions } from '../../llm/slice';
/**
 * Initial states of notification function are defined here
 */
export const initialState = {
  isEnabled: false,
  message: '',
  type: ERROR_TYPES.SUCCESS,
};
/**
 * All actions related to notification are defined here
 */
export const notificationSlice = createSlice({
  name: 'feature/notification',
  initialState,
  reducers: {
    resetNotification(state) {
      state.isEnabled = false;
      state.message = '';
      state.type = ERROR_TYPES.SUCCESS;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(signInActions.signIn, (state) => ({
        ...state,
        ...initialState,
      }))
      .addCase(signInActions.signInFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.WARNING,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(forgotPasswordActions.forgotPasswordSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(verifyCallbackActions.verifyTokenFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(resetPasswordActions.resetPasswordSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(resetPasswordActions.resetPasswordFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(visualActions.createVisualsFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(visualActions.createVisualsSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(visualActions.deleteVisualFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(visualActions.deleteVisualSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(visualActions.updateVisualFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(visualActions.updateVisualSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(scenarioActions.createScenariosFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(scenarioActions.createScenariosSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(scenarioActions.deleteScenarioFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(scenarioActions.deleteScenarioSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(scenarioActions.updateScenarioFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(scenarioActions.updateScenarioSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(visualFilterActions.createVisualFilterSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(visualFilterActions.createVisualFilterFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(visualFilterActions.updateVisualFilterFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(visualFilterActions.updateVisualFilterSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(visualFilterActions.deleteVisualFilterSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(visualFilterActions.deleteVisualFilterFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(usersActions.inviteUserSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(usersActions.inviteUserFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(userInvitesActions.resendInviteSucceeded, (state, action) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: action?.payload,
        isEnabled: true,
      }))
      .addCase(userInvitesActions.resendInviteFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(usersActions.changeUserPasswordSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Password Changed Successfully',
        isEnabled: true,
      }))
      .addCase(usersActions.changeUserPasswordFailed, (state) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: 'Password Change Failed!',
        isEnabled: true,
      }))
      .addCase(usersActions.createUsersFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(usersActions.createUsersSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'User successfully created',
        isEnabled: true,
      }))
      .addCase(usersActions.createGeneralUsersFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(usersActions.createGeneralUsersSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'User successfully created',
        isEnabled: true,
      }))
      .addCase(usersActions.createAdminUsersFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(usersActions.createAdminUsersSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'User successfully created',
        isEnabled: true,
      }))
      .addCase(usersActions.updateUserFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(usersActions.updateUserSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'User successfully updated',
        isEnabled: true,
      }))
      .addCase(usersActions.deleteUserFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(usersActions.deleteUserSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'User successfully deleted',
        isEnabled: true,
      }))
      .addCase(hotelActions.updateHotelFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(hotelActions.updateHotelSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Hotel successfully updated',
        isEnabled: true,
      }))
      .addCase(hotelActions.createHotelFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(hotelActions.createHotelSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Hotel successfully created',
        isEnabled: true,
      }))
      .addCase(systemActions.createSystemInformationSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Announcement Information successfully created',
        isEnabled: true,
      }))
      .addCase(systemActions.createSystemInformationFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(systemActions.updateSystemInformationSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Announcement Information successfully updated',
        isEnabled: true,
      }))
      .addCase(systemActions.updateSystemInformationFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(systemActions.syncDatabricksDataSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Triggered Sync Databricks Data successfully',
        isEnabled: true,
      }))
      .addCase(systemActions.syncDatabricksDataFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(systemActions.syncBreakdownDataSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Triggered Sync Breakdown Data successfully',
        isEnabled: true,
      }))
      .addCase(systemActions.syncBreakdownDataFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(systemActions.syncCurrencyDataSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Triggered Sync Currency Data successfully',
        isEnabled: true,
      }))
      .addCase(systemActions.syncCurrencyDataFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(hotelSetActions.createHotelGroupFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(hotelSetActions.createHotelGroupSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Hotel set successfully created',
        isEnabled: true,
      }))
      .addCase(hotelSetActions.deleteHotelGroupFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(hotelSetActions.deleteHotelGroupSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Hotel set successfully deleted',
        isEnabled: true,
      }))
      .addCase(llmActions.createModelFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(llmActions.createModelSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Model successfully created',
        isEnabled: true,
      }))
      .addCase(llmActions.getModelUsageFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(llmActions.createDefaultModelsInformationSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Default models successfully created',
        isEnabled: true,
      }))
      .addCase(llmActions.createDefaultModelsInformationFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      }))
      .addCase(llmActions.updateDefaultModelsInformationSucceeded, (state) => ({
        ...state,
        type: ERROR_TYPES.SUCCESS,
        message: 'Default models successfully updated',
        isEnabled: true,
      }))
      .addCase(llmActions.updateDefaultModelsInformationFailed, (state, action) => ({
        ...state,
        type: ERROR_TYPES.ERROR,
        message: action?.payload?.message,
        isEnabled: true,
      })) ;
  },
});
//
export const { actions: notificationActions } = notificationSlice;
